import i18next from 'i18next';

import { UserRights } from '@work4all/models/lib/additionalEnums/UserRights';
import { ModuleAccessRight } from '@work4all/models/lib/Classes/ModuleAccessRight.entity';

import {
  makeCanPerformEntityOperation,
  makeHasAnyRightOf,
} from '@work4all/utils/lib/permissions';

import { checkModuleRight } from '../../useCanView';
import { IUser } from '../../useUser';

import {
  GetPermissionStateProps,
  GetPermissionStateReturn,
  PermissionRequiredRight,
} from './types';

export const getPermissionState = ({
  user,
  record,
  rights,
  requiredRights,
  withExtraInfos = false,
}: GetPermissionStateProps): GetPermissionStateReturn => {
  let allowed = true;
  const unauthorizedRights: PermissionRequiredRight[] = [];

  requiredRights.forEach((requiredRight) => {
    if (Array.isArray(requiredRight)) {
      const _unauthorizedRights = requiredRight.filter((right) => {
        return !hasRight(user, right, record, rights);
      });

      if (_unauthorizedRights.length === requiredRight.length) {
        unauthorizedRights.push(..._unauthorizedRights);
        allowed = false;
      }

      return;
    }

    const isUnauthorizedRight = !hasRight(user, requiredRight, record, rights);

    if (isUnauthorizedRight) {
      unauthorizedRights.push(requiredRight);
      allowed = false;
    }
  }, []);

  if (!withExtraInfos) {
    return allowed;
  }

  return {
    allowed,
    reason: allowed ? undefined : getReason(unauthorizedRights),
  };
};

const hasRight = (
  user: IUser,
  right: PermissionRequiredRight,
  record: unknown,
  rights: ModuleAccessRight[]
): boolean => {
  let result = false;

  switch (right.type) {
    case 'UserRight':
      result = makeHasAnyRightOf([right.value as UserRights])(user);
      break;
    case 'UserRightOperation':
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      result = makeCanPerformEntityOperation<any>({
        anyPermissionCode: right.value,
        ownlyOwnPermissionCode: right.ownlyOwnPermissionCode,
        ownageProperty: right.ownageProperty,
      })(user, record);
      break;
    case 'ModuleAccessRight':
      result = checkModuleRight(rights, right.value);
      break;
  }

  if (right.mustNotHave) {
    return !result;
  }

  return result;
};

const getReason = (unauthorizedRights: PermissionRequiredRight[]) => {
  let missingMessage = '';
  const unauthorizedUserRights = unauthorizedRights.filter(
    (unauthorizedRight) => unauthorizedRight.type !== 'ModuleAccessRight'
  );

  const unauthorizedModuleAccessRights = unauthorizedRights.filter(
    (unauthorizedRight) => unauthorizedRight.type === 'ModuleAccessRight'
  );

  const missingRightsMessage = i18next.t('USE_PERMISSIONS.RIGHT_MISSING', {
    count: unauthorizedUserRights.length,
    missing: unauthorizedUserRights
      .map((unauthorizedRight) =>
        i18next.t(`USE_PERMISSIONS.USER_RIGHTS.${unauthorizedRight.value}`)
      )
      .join('\n'),
  });

  const missingLicenseMessage = i18next.t('USE_PERMISSIONS.LICENSE_MISSING', {
    count: unauthorizedModuleAccessRights.length,
    missing: unauthorizedModuleAccessRights
      .map((unauthorizedRight) => unauthorizedRight.value)
      .join('\n'),
  });

  if (
    unauthorizedUserRights.length > 0 &&
    unauthorizedModuleAccessRights.length > 0
  ) {
    missingMessage = `${missingRightsMessage} ${i18next.t(
      'USE_PERMISSIONS.AND_THE'
    )} ${missingLicenseMessage}`;
  } else if (unauthorizedUserRights.length > 0) {
    missingMessage = missingRightsMessage;
  } else {
    missingMessage = missingLicenseMessage;
  }

  return `${missingMessage}.\n${i18next.t(
    'USE_PERMISSIONS.CONTACT_YOUR_ADMINISTRATOR'
  )}`;
};
